import React, { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppBarMenu from "../../components/AppBarMenu";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import ResourceList from "../../components/shared/ResourceList";
import PowerCompanyDrawer from "../../components/management/PowerCompanyDrawer";
import { ApiDataContext } from "../../contexts/ApiDataContext";

/**
 * Página dedicada para gestão de concessionárias de energia
 * Usa ResourceList para listagem e PowerCompanyDrawer apenas para criar
 * Edição é feita em página própria
 */
export default function PowerCompaniesPage() {
  const { apiData } = useContext(ApiDataContext);
  const navigate = useNavigate();
  
  // Estado do drawer apenas para CRIAR
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createdPowerCompanyId, setCreatedPowerCompanyId] = useState(null);

  // Função de busca client-side (concessionárias vêm do ApiDataContext)
  const fetchPowerCompanies = useCallback(async ({ page, limit, search }) => {
    const powerCompanies = apiData.powerDistributionCompanies || [];
    
    // Filtrar por search
    let filtered = powerCompanies;
    if (search && search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = powerCompanies.filter(pdc => 
        pdc.name?.toLowerCase().includes(searchLower) ||
        pdc.states?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Paginar client-side (page começa em 1 no ResourceList)
    const start = (page - 1) * limit;
    const end = start + limit;
    const paginated = filtered.slice(start, end).map(pdc => ({
      ...pdc,
      _id: pdc.id // ResourceList espera _id
    }));

    return {
      data: paginated,
      pagination: {
        total: filtered.length,
        page,
        limit
      }
    };
  }, [apiData.powerDistributionCompanies]);

  // Colunas da tabela
  const columns = [
    { 
      header: "Nome", 
      accessor: "name",
      align: "left"
    },
    { 
      header: "Estado", 
      accessor: (pdc) => pdc.states?.name || "-",
      align: "left"
    },
    { 
      header: "Processamento", 
      accessor: (pdc) => pdc.processing === 1 ? "Sim" : "Não",
      align: "center"
    },
    { 
      header: "Valor ART Diferente", 
      accessor: (pdc) => pdc.isArtDifferent === 1 ? "Sim" : "Não",
      align: "center"
    },
  ];

  const handleCreatePowerCompany = () => {
    setDrawerOpen(true);
  };

  const handleRowClick = (powerCompany) => {
    // Navegar para página de edição
    navigate(`/power-companies/${powerCompany.id}`);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    // Se criou uma concessionária, aguarda um pouco e redireciona
    if (createdPowerCompanyId) {
      setTimeout(() => {
        navigate(`/power-companies/${createdPowerCompanyId}`);
        setCreatedPowerCompanyId(null);
      }, 1000);
    }
  };

  return (
    <AppBarMenu>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <h1 style={{ margin: 0 }}>Concessionárias</h1>
            <p style={{ margin: "8px 0 0 0", color: "text.secondary" }}>
              Gerencie as concessionárias de energia cadastradas no sistema
            </p>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePowerCompany}
            sx={{ height: 40 }}
          >
            Nova Concessionária
          </Button>
        </Box>

        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <ResourceList
            fetchData={fetchPowerCompanies}
            columns={columns}
            searchParam="search"
            resourceName="Concessionárias"
            onRowClick={handleRowClick}
            storageKey="power-companies"
          />
        </Paper>

        {/* Drawer apenas para criar concessionárias */}
        <PowerCompanyDrawer
          open={drawerOpen}
          onClose={handleCloseDrawer}
          mode="create"
          onPowerCompanyCreated={(id) => {
            setCreatedPowerCompanyId(id);
          }}
        />
      </Container>
    </AppBarMenu>
  );
}
